import { useCallback, useState } from "react";
import { useAtom } from "jotai";
import { venueSearchParamsAtom } from "../business/home/atoms";
import { getFirstResultLocationAndAddress, addRecentSearch } from "../../apis/search";
import { defaultRegion } from "../../config";
import { Storage } from "../../storage";

export interface SavedSearch {
  id: string | number;
  name: string;
  description?: string;
  search_term?: string;
  filters?: {
    business_types?: string[] | string;
    accessibilities?: number[] | string;
    opening_hours?: {
      day: number[];
      start: string;
      end: string;
    };
    open_now?: boolean;
    // New parameters
    latitude?: string;
    longitude?: string;
    address?: string;
    search_type?: string;
    search_value?: string;
    tagging_mode?: string;
    placesToReplaceString?: string;
  };
}

export interface UseSavedSearchSelectResult {
  handleSelect: (search: SavedSearch) => Promise<boolean>;
  isLoading: boolean;
  error: string | null;
}

/**
 * Centralized hook for handling saved search selection
 * 
 * This hook:
 * 1. Parses filters from saved search
 * 2. Uses stored coordinates if available, otherwise geocodes the search term
 * 3. Updates venueSearchParamsAtom with location, filters, and search context
 * 4. Saves to recent searches with stored parameters
 * 5. Returns success flag for navigation handling
 * 
 * @returns Object with handleSelect function, loading state, and error
 */
export function useSavedSearchSelect(): UseSavedSearchSelectResult {
  const [searchPageParams, setSearchPageParams] = useAtom(venueSearchParamsAtom);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSelect = useCallback(
    async (search: SavedSearch): Promise<boolean> => {
      try {
        setIsLoading(true);
        setError(null);

        // Parse filters from saved search
        // Handle both string and array formats for business_types
        const typesStr = Array.isArray(search.filters?.business_types)
          ? search.filters.business_types.join("|")
          : (search.filters?.business_types || "");
        
        // Handle both string and array formats for accessibilities
        const accessibilitiesStr = Array.isArray(search.filters?.accessibilities)
          ? search.filters.accessibilities.map((id: number) => id.toString()).join("|")
          : (search.filters?.accessibilities || "");

        // Determine showVenueOptions based on filters
        let showVenueOptions = "All";
        if (search.filters?.open_now) {
          showVenueOptions = "Open Now";
        } else if (search.filters?.opening_hours) {
          // If specific opening hours are set, use Custom
          showVenueOptions = "Custom";
        }

        const searchTerm = search.search_term || "";

        // Get current search center coordinates to bias search results
        const searchLat = 
          parseFloat(searchPageParams.latitude || "") || defaultRegion.latitude;
        const searchLng = 
          parseFloat(searchPageParams.longitude || "") || defaultRegion.longitude;

        // Use stored coordinates if available, otherwise geocode
        let lat = searchLat;
        let lng = searchLng;
        let address = "";

        // Use stored coordinates if available
        if (search.filters?.latitude && search.filters?.longitude) {
          lat = parseFloat(search.filters.latitude);
          lng = parseFloat(search.filters.longitude);
          address = search.filters.address || search.filters.placesToReplaceString || "";
        } else if (searchTerm) {
          // Geocode only if coordinates not stored (backwards compatibility)
          try {
            const result = await getFirstResultLocationAndAddress(
              searchTerm,
              searchLat,
              searchLng
            );

            if (!result.error) {
              lat = result.lat;
              lng = result.lng;
              address = result.address;
            } else {
              // If geocoding fails, log error but continue with current location
              console.warn("Geocoding failed for saved search, using current location:", result.error);
            }
          } catch (geocodingError) {
            // Fall back to current location if API call fails
            console.error("Failed to geocode saved search term:", geocodingError);
          }
        }

        // Store recent search if user is logged in and has a search term
        const accessToken = Storage.getCurrentUserToken();
        if (accessToken && searchTerm) {
          try {
            // Determine opening hours parameters
            const openingHoursDay = search.filters?.opening_hours?.day?.[0]?.toString() || "";
            const openingHoursStartTime = search.filters?.opening_hours?.start || "";

            await addRecentSearch({
              searchTerm: searchTerm,
              types: typesStr,
              accessibilities: accessibilitiesStr,
              showVenueOptions: showVenueOptions,
              openingHoursDay: openingHoursDay,
              openingHoursStartTime: openingHoursStartTime,
              openingHoursEndTime: "",
              // Use stored geocoding or geocoded values
              latitude: lat.toString(),
              longitude: lng.toString(),
              address: address,
              // Use stored search context or fallback to defaults
              search_type: search.filters?.search_type || "search",
              search_value: search.filters?.search_value || searchTerm,
              tagging_mode: search.filters?.tagging_mode || "off",
              placesToReplaceString: address,
            }, accessToken);
          } catch (recentSearchError) {
            // Don't fail the whole operation if saving recent search fails
            console.error("Failed to save recent search:", recentSearchError);
          }
        }

        // Update home search params with geocoded location and filters
        setSearchPageParams((prev) => ({
          ...prev,
          search: searchTerm,
          latitude: lat.toString(),
          longitude: lng.toString(),
          placesToReplaceString: address,
          address: search.filters?.address || address,
          types: typesStr,
          accessibilities: accessibilitiesStr,
          // Saved searches store tags only; never carry a previous section selection over.
          accessibility_sections: "",
          showVenueOptions,
          // Use stored parameters or fallback to defaults
          search_type: search.filters?.search_type || "search",
          search_value: search.filters?.search_value || searchTerm,

          //taggign mode to be picked based on the mapper status - existing value set
          // tagging_mode: search.filters?.tagging_mode || "off",

          autoZoom: "yes",
          includeGoogleFallback: "false",
          unixTimestamp: Math.floor(Date.now() / 1000),
        }));

        setIsLoading(false);
        return true;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to select saved search";
        setError(errorMessage);
        console.error("Error in handleSavedSearchSelect:", err);
        setIsLoading(false);
        return false;
      }
    },
    [searchPageParams, setSearchPageParams]
  );

  return {
    handleSelect,
    isLoading,
    error,
  };
}
